// Integer arithmetic mirrors server Decimal rules; all monetary accumulators are cents.
const SCALE = 1000000n;
const round = (n, d) => (n + d / 2n) / d;
const decimal = (value) => {
  const text = String(value ?? "0");
  if (!/^\d*(\.\d{0,6})?$/.test(text)) return 0n;
  const [whole, fraction = ""] = text.split(".");
  return BigInt(whole || "0") * SCALE + BigInt(fraction.padEnd(6, "0"));
};
export const categories = ["labour", "plant", "material", "subcontract", "other"];
const keys = [...categories, "direct_cost", "allowances", "overheads", "total_cost", "sell_price"];
const empty = () => Object.fromEntries(keys.map((k) => [k, 0n]));
const finish = (t) => {
  const values = Object.fromEntries(Object.entries(t).map(([k, v]) => [k, Number(v) / 100]));
  values.gross_profit = values.sell_price - values.total_cost;
  values.margin = values.sell_price ? values.gross_profit / values.sell_price * 100 : 0;
  return values;
};
export const resourceCost = (r) => Number(round(decimal(r.quantity) * decimal(r.unit_cost) * 100n, SCALE * SCALE)) / 100;
export function estimateTotals(estimate) {
  const total = empty(), sections = {}, items = {};
  for (const section of estimate.sections) {
    const st = empty();
    for (const item of section.items) {
      const it = empty();
      for (const r of item.resources) it[r.type] += round(decimal(r.quantity) * decimal(r.unit_cost) * 100n, SCALE * SCALE);
      it.direct_cost = categories.reduce((n, k) => n + it[k], 0n);
      it.allowances = item.allowance_mode === "fixed" ? round(decimal(item.allowance) * 100n, SCALE) : round(it.direct_cost * decimal(item.allowance), 100n * SCALE);
      it.overheads = round((it.direct_cost + it.allowances) * decimal(estimate.settings.overhead_percent), 100n * SCALE);
      it.total_cost = it.direct_cost + it.allowances + it.overheads;
      const denominator = 100n * SCALE - decimal(item.margin ?? estimate.settings.default_margin);
      const factor = estimate.settings.rounding === "dollar" ? 100n : 1n;
      it.sell_price = denominator > 0n ? round(it.total_cost * 100n * SCALE, denominator * factor) * factor : 0n;
      keys.forEach((k) => { st[k] += it[k]; });
      items[item.id] = { ...finish(it), sell_rate: decimal(item.quantity) ? Number(round(it.sell_price * SCALE, decimal(item.quantity))) / 100 : null };
    }
    keys.forEach((k) => { total[k] += st[k]; });
    sections[section.id] = finish(st);
  }
  return { total: finish(total), sections, items };
}
export function validateEstimate(estimate) {
  const check = (v, max, label) => {
    if (!/^\d+(\.\d{0,6})?$/.test(String(v)) || Number(v) > max) throw new Error(`${label}: enter a non-negative number with up to six decimal places (maximum ${max}).`);
  };
  check(estimate.settings.default_margin, 99.999999, "Default margin");
  check(estimate.settings.overhead_percent, 1000, "Overheads");
  for (const s of estimate.sections) {
    if (!s.name.trim()) throw new Error("Give each section a name.");
    for (const i of s.items) {
      if (!i.description.trim()) throw new Error("Give each cost item a description.");
      check(i.quantity, 1e9, "Item quantity"); check(i.allowance, 1e9, "Allowance");
      if (i.margin != null) check(i.margin, 99.999999, "Item margin");
      for (const r of i.resources) { if (!r.description.trim()) throw new Error("Give each resource a description."); check(r.quantity, 1e9, "Resource quantity"); check(r.unit_cost, 1e9, "Unit cost"); }
    }
  }
}
